import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Input, Select } from 'antd';
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';

import GetField from '../Components/GetField';
import styles from '../styles';
import ShowFiles from './ShowFiles';
import { deleteQualityCheckItem } from '../Actions';

const AntSelect = GetField(Select);
const AntInput = GetField(Input);
const { Option } = Select;

const StyleForm = props => {
  const {
    classes,
    getValues,
    setValue,
    control,
    watch,
    styleItem,
    styleIndex,
    styleAppend,
    styleRemove,
    unregister,
  } = props;

  const dispatch = useDispatch();

  const [FileModal, SetFileModal] = useState(false);

  const stylesTable = useSelector(
    state => state.inwardItemsForQualityCheckInputTable.styles
  );

  const stylesArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);

    if (stylesTable.styles[productId] !== undefined) {
      return stylesTable.styles[productId].styleNames || [];
    }
    return [];
  }, [stylesTable, watch(`styles[${styleIndex}].productId`)]);

  const descriptionsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const style = getValues(`styles[${styleIndex}].style`);

    if (stylesTable.styles[productId] !== undefined) {
      return stylesTable.styles[productId].descriptions[style] || [];
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
  ]);

  const sizesArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const style = getValues(`styles[${styleIndex}].style`);
    const description = getValues(`styles[${styleIndex}].description`);

    if (stylesTable.styles[productId]?.sizes[style] !== undefined) {
      return stylesTable.styles[productId].sizes[style][description] || [];
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
  ]);

  const baseColorsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const styleId = getValues(`styles[${styleIndex}].styleId`);

    if (stylesTable.styles[productId] !== undefined) {
      return stylesTable.styles[productId].baseColors[styleId] || [];
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
  ]);

  const printColorsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const styleId = getValues(`styles[${styleIndex}].styleId`);
    const baseColorId = getValues(`styles[${styleIndex}].baseColorId`);

    if (stylesTable.styles[productId]?.printColors[styleId]?.[baseColorId]) {
      return (
        stylesTable.styles[productId].printColors[styleId][baseColorId] || []
      );
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
    watch(`styles[${styleIndex}].baseColorId`),
  ]);

  const embroideryColorsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const styleId = getValues(`styles[${styleIndex}].styleId`);
    const baseColorId = getValues(`styles[${styleIndex}].baseColorId`);
    const printColorId = getValues(`styles[${styleIndex}].printColorId`);

    if (
      stylesTable.styles[productId]?.embroideryColors[styleId]?.[baseColorId]?.[
        printColorId
      ]
    ) {
      return (
        stylesTable.styles[productId].embroideryColors[styleId][baseColorId][
          printColorId
        ] || []
      );
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
    watch(`styles[${styleIndex}].baseColorId`),
    watch(`styles[${styleIndex}].printColorId`),
  ]);

  const acidWashColorsArray = useMemo(() => {
    const productId = getValues(`styles[${styleIndex}].productId`);
    const styleId = getValues(`styles[${styleIndex}].styleId`);
    const baseColorId = getValues(`styles[${styleIndex}].baseColorId`);
    const printColorId = getValues(`styles[${styleIndex}].printColorId`);
    const embroideryColorId = getValues(
      `styles[${styleIndex}].embroideryColorId`
    );

    if (
      stylesTable.styles[productId]?.acidWashColors[styleId]?.[baseColorId]?.[
        printColorId
      ]?.[embroideryColorId]
    ) {
      return (
        stylesTable.styles[productId].acidWashColors[styleId][baseColorId][
          printColorId
        ][embroideryColorId] || []
      );
    }
    return [];
  }, [
    stylesTable,
    watch(`styles[${styleIndex}].productId`),
    watch(`styles[${styleIndex}].style`),
    watch(`styles[${styleIndex}].description`),
    watch(`styles[${styleIndex}].styleId`),
    watch(`styles[${styleIndex}].baseColorId`),
    watch(`styles[${styleIndex}].printColorId`),
    watch(`styles[${styleIndex}].embroideryColorId`),
  ]);

  return (
    <>
      <tr>
        <td>
          <Controller
            name={`styles[${styleIndex}].productId`}
            control={control}
            defaultValue={styleItem.productId}
            render={({ field }) => (
              <AntSelect placeholder="Select product" {...field}>
                {stylesTable.products.map(({ name, id }) => (
                  <Option key={id} value={id}>
                    {name}
                  </Option>
                ))}
              </AntSelect>
            )}
          />
        </td>
        <td className={classes.inputTd}>
          <Controller
            name={`styles[${styleIndex}].style`}
            control={control}
            defaultValue={styleItem.style}
            render={({ field }) => (
              <AntSelect placeholder="Select style" {...field}>
                {stylesArray.map(style => (
                  <Option key={style} value={style}>
                    {style}
                  </Option>
                ))}
              </AntSelect>
            )}
          />
        </td>
        <td className={classes.inputTd}>
          <Controller
            name={`styles[${styleIndex}].description`}
            control={control}
            defaultValue={styleItem.description}
            render={({ field }) => (
              <AntSelect placeholder="Select description" {...field}>
                {descriptionsArray.map(description => (
                  <Option key={description} value={description}>
                    {description}
                  </Option>
                ))}
              </AntSelect>
            )}
          />
        </td>
        <td className={classes.inputTd}>
          <Controller
            name={`styles[${styleIndex}].styleId`}
            control={control}
            defaultValue={styleItem.styleId}
            render={({ field }) => (
              <AntSelect placeholder="Select style" {...field}>
                {sizesArray.map(size => (
                  <Option key={size.id} value={size.id}>
                    {size.size}
                  </Option>
                ))}
              </AntSelect>
            )}
          />
        </td>
        <td className={classes.inputTd}>
          <Controller
            name={`styles[${styleIndex}].baseColorId`}
            control={control}
            defaultValue={styleItem.baseColorId}
            render={({ field }) => (
              <AntSelect placeholder="Select color" {...field}>
                {baseColorsArray.map(color => (
                  <Option key={color.id} value={color.id}>
                    {color.color}
                  </Option>
                ))}
              </AntSelect>
            )}
          />
        </td>
        <td className={classes.inputTd}>
          <Controller
            name={`styles[${styleIndex}].printColorId`}
            control={control}
            defaultValue={styleItem.printColorId}
            render={({ field }) => (
              <AntSelect placeholder="Select color" {...field}>
                {printColorsArray.map(color => (
                  <Option key={color.id} value={color.id}>
                    {color.color}
                  </Option>
                ))}
              </AntSelect>
            )}
          />
        </td>
        <td className={classes.inputTd}>
          <Controller
            name={`styles[${styleIndex}].embroideryColorId`}
            control={control}
            defaultValue={styleItem.embroideryColorId}
            render={({ field }) => (
              <AntSelect placeholder="Select color" {...field}>
                {embroideryColorsArray.map(color => (
                  <Option key={color.id} value={color.id}>
                    {color.color}
                  </Option>
                ))}
              </AntSelect>
            )}
          />
        </td>
        <td className={classes.inputTd}>
          <Controller
            name={`styles[${styleIndex}].acidWashColorId`}
            control={control}
            defaultValue={styleItem.acidWashColorId}
            render={({ field }) => (
              <AntSelect placeholder="Select color" {...field}>
                {acidWashColorsArray.map(color => (
                  <Option key={color.id} value={color.id}>
                    {color.color}
                  </Option>
                ))}
              </AntSelect>
            )}
          />
        </td>
        <td className={classes.inputTd}>
          <Controller
            name={`styles[${styleIndex}].quantity`}
            control={control}
            defaultValue={styleItem.quantity}
            render={({ field }) => (
              <AntInput
                {...field}
                placeholder="Quantity"
                type="number"
                min={0}
                size="small"
              />
            )}
          />
        </td>
        <td className={classes.inputTd}>
          <Controller
            name={`styles[${styleIndex}].condition`}
            control={control}
            defaultValue={styleItem.condition}
            render={({ field }) => (
              <AntSelect
                placeholder="Select color"
                {...field}
                onChange={value => {
                  field.onChange(value);
                  if (value === 'rejected') {
                    setValue(`styles[${styleIndex}].factoryId`, null);
                    setValue(`styles[${styleIndex}].racknoId`, null);
                  }
                }}
              >
                <Option key="rejected" value="rejected">
                  Rejected
                </Option>
                <Option key="accepted" value="accepted">
                  Accepted
                </Option>
                <Option key="altered" value="altered">
                  Altered
                </Option>
              </AntSelect>
            )}
          />
        </td>
        <td className={classes.inputTd}>
          <Controller
            name={`styles[${styleIndex}].remark`}
            control={control}
            defaultValue={styleItem.remark}
            render={({ field }) => <AntInput placeholder="Remark" {...field} />}
          />
        </td>
        <td className={classes.inputTd}>
          <Button onClick={() => SetFileModal(true)}>File</Button>
        </td>
        <td className={classes.inputTd}>
          <DeleteOutlined
            onClick={() => {
              const stylesItems = getValues('styles');
              if (stylesItems && stylesItems.length > 1) {
                styleRemove(styleIndex);
                dispatch(deleteQualityCheckItem('styles', styleIndex));
              }
            }}
          />
          &nbsp; &nbsp;
          <PlusCircleOutlined
            onClick={() =>
              styleAppend({
                productId: null,
                styleId: null,
                baseColorId: null,
                printColorId: null,
                embroideryColorId: null,
                acidWashColorId: null,
              })
            }
          />
        </td>
      </tr>
      {FileModal ? (
        <ShowFiles
          itemIndex={styleIndex}
          itemType="styles"
          watch={watch}
          setValue={setValue}
          unregister={unregister}
          SetFileModal={SetFileModal}
        />
      ) : null}
    </>
  );
};

StyleForm.propTypes = {
  classes: PropTypes.object.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  control: PropTypes.any.isRequired,
  watch: PropTypes.func.isRequired,
  unregister: PropTypes.func.isRequired,
  styleRemove: PropTypes.func.isRequired,
  styleAppend: PropTypes.func.isRequired,
  styleItem: PropTypes.object.isRequired,
  styleIndex: PropTypes.number.isRequired,
};

export default withStyles(styles)(StyleForm);
